import { formatSecondsToHMS } from '../../helpers/date/date-helper';

const BAD_TOOL_USAGE_ALERT_TYPE = 'bad_tool_usage';
const MULTIPLE_PIECES_ALERT_TYPE = 'multiple_pieces';
const MISSING_ALERT_PREFIX = 'missing_';

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const getDate = (value) => {
  if (value instanceof Date) return value;

  if (typeof value === 'number') {
    return new Date(value > 1000000000000 ? value : value * 1000);
  }

  return new Date(value);
};

const getDateMilliseconds = (value) => {
  const date = getDate(value);
  const timestamp = date.getTime();

  return Number.isNaN(timestamp) ? null : timestamp;
};

const getNumericValue = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const getAverage = (values) => {
  const validValues = values.filter(Number.isFinite);

  if (validValues.length === 0) return null;

  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
};

const getLocalEvolutionKey = (value, hourly = false) => {
  const date = getDate(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (!hourly) return `${year}-${month}-${day}`;

  const hour = String(date.getHours()).padStart(2, '0');

  return `${year}-${month}-${day}-${hour}`;
};

const formatLocalEvolutionLabel = (key, hourly = false, full = false) => {
  const [year, month, day, hour] = key.split('-').map(Number);
  const date = new Date(year, month - 1, day, hour ?? 0);

  if (hourly) {
    return new Intl.DateTimeFormat('fr-FR', full
      ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
      : { hour: '2-digit', minute: '2-digit' }).format(date);
  }

  return new Intl.DateTimeFormat('fr-FR', full
    ? { day: '2-digit', month: '2-digit', year: 'numeric' }
    : { day: '2-digit', month: '2-digit' }).format(date);
};

const isCompletedPiece = (piece) => {
  return piece?.process_end && getDateMilliseconds(piece.process_end) !== null && getNumericValue(piece.process_duration) !== null;
};

const buildAlertsByPieceId = (alerts) => {
  const alertsByPieceId = new Map();

  alerts.forEach((alert) => {
    const key = String(alert.piece_id);

    if (!alertsByPieceId.has(key)) alertsByPieceId.set(key, []);

    alertsByPieceId.get(key).push(alert);
  });

  return alertsByPieceId;
};

const getPieceAlerts = (piece, alertsByPieceId) => {
  return alertsByPieceId.get(String(piece.id)) ?? [];
};

const getAlertsForPieces = (pieces, alertsByPieceId) => {
  return pieces.flatMap((piece) => getPieceAlerts(piece, alertsByPieceId));
};

const sortAlertsAscending = (alerts) => {
  return [...alerts].sort((alertA, alertB) => {
    return getDateMilliseconds(alertA.timestamp) - getDateMilliseconds(alertB.timestamp);
  });
};

export const formatVACAeroDashboardInfluxPieces = (rows) => {
  const piecesById = new Map();

  rows.forEach((row) => {
    if (!row?.piece_id) return;
    if (!['process_start', 'process_end', 'process_duration'].includes(row._field)) return;

    const pieceId = String(row.piece_id);
    const piece = piecesById.get(pieceId) ?? { id: pieceId };

    if (row._field === 'process_start') piece.process_start = getNumericValue(row._value);
    if (row._field === 'process_end') piece.process_end = getNumericValue(row._value);
    if (row._field === 'process_duration') piece.process_duration = getNumericValue(row._value);

    piecesById.set(pieceId, piece);
  });

  return [...piecesById.values()].filter((piece) => {
    return piece.process_start !== null && piece.process_start !== undefined &&
      piece.process_end !== null && piece.process_end !== undefined &&
      piece.process_duration !== null && piece.process_duration !== undefined;
  });
};

export const formatVACAeroDashboardInfluxAlerts = (rows) => {
  return rows
    .filter((row) => row?.piece_id && row?._field === 'type' && row?._value)
    .map((row, index) => {
      return {
        id: `${row.piece_id}-${row._time}-${index}`,
        piece_id: String(row.piece_id),
        type: String(row._value),
        timestamp: row._time,
      };
    });
};

export const getVACAeroDashboardPatternEventTypes = (pattern) => {
  return pattern?.pattern_event_types ?? pattern?.patternEventTypes ?? [];
};

const getPatternEventTypeEventType = (patternEventType) => {
  return patternEventType?.event_type ?? patternEventType?.eventType ?? null;
};

export const getVACAeroDashboardEventTypes = (pattern) => {
  return getVACAeroDashboardPatternEventTypes(pattern).map(getPatternEventTypeEventType).filter(Boolean);
};

export const getVACAeroDashboardTheoreticalCycleTime = (pattern) => {
  const patternData = pattern?.pattern_data ?? pattern?.patternData ?? [];
  const durationData = patternData.find((data) => data?.name === 'duration');
  const duration = getNumericValue(durationData?.value);

  return duration !== null && duration > 0 ? duration : null;
};

export const getVACAeroDashboardStepCount = (pattern) => {
  return getVACAeroDashboardPatternEventTypes(pattern).length;
};

export const getVACAeroDashboardToolStepCount = (pattern) => {
  return getVACAeroDashboardPatternEventTypes(pattern).filter((patternEventType) => Number(patternEventType?.quantity) > 0).length;
};

export const getVACAeroDashboardAlertLabel = (alertType, pattern) => {
  if (alertType === BAD_TOOL_USAGE_ALERT_TYPE) return 'Utilisation d\'un outil incorrect';
  if (alertType === MULTIPLE_PIECES_ALERT_TYPE) return 'Plusieurs pièces détectées';

  if (typeof alertType === 'string' && alertType.startsWith(MISSING_ALERT_PREFIX)) {
    const subtype = alertType.slice(MISSING_ALERT_PREFIX.length);

    const eventType = getVACAeroDashboardEventTypes(pattern).find((currentEventType) => {
      return String(currentEventType.subtype) === String(subtype);
    });

    return `Etape manquée : ${eventType?.name ?? 'Opération inconnue'}`;
  }

  return alertType ?? 'Alerte inconnue';
};

export const getVACAeroDashboardDefaultFilters = (pieces) => {
  const completedPieces = pieces.filter(isCompletedPiece);

  if (completedPieces.length === 0) {
    const now = Math.floor(Date.now() / 1000);

    return {
      start: now - 30 * 24 * 60 * 60,
      end: now,
      rangeKey: 'last_30d',
    };
  }

  const timestamps = completedPieces.map((piece) => getDateMilliseconds(piece.process_end));

  return {
    start: Math.floor(Math.min(...timestamps) / 1000),
    end: Math.ceil(Math.max(...timestamps) / 1000),
    rangeKey: 'all',
  };
};

export const getVACAeroDashboardPeriodRange = (rangeKey, pieces) => {
  const now = Math.floor(Date.now() / 1000);

  if (rangeKey === 'all') return getVACAeroDashboardDefaultFilters(pieces);

  const durations = {
    last_24h: 24 * 60 * 60,
    last_7d: 7 * 24 * 60 * 60,
    last_30d: 30 * 24 * 60 * 60,
  };

  const duration = durations[rangeKey];

  if (!duration) return getVACAeroDashboardDefaultFilters(pieces);

  return {
    start: now - duration,
    end: now,
    rangeKey,
  };
};

export const filterVACAeroDashboardPieces = (pieces, filters) => {
  const startMilliseconds = filters.start * 1000;
  const endMilliseconds = filters.end * 1000;

  return pieces.filter((piece) => {
    if (!isCompletedPiece(piece)) return false;

    const processEnd = getDateMilliseconds(piece.process_end);

    return processEnd >= startMilliseconds && processEnd <= endMilliseconds;
  });
};

export const sortVACAeroDashboardPieces = (pieces) => {
  return [...pieces].sort((pieceA, pieceB) => {
    return getDateMilliseconds(pieceB.process_end) - getDateMilliseconds(pieceA.process_end);
  });
};

const getPieceScoreMap = (pieces, alertsByPieceId, stepCount, theoreticalCycleTime) => {
  const scoreByPieceId = new Map();

  if (stepCount <= 0) return scoreByPieceId;

  pieces.filter(isCompletedPiece).forEach((piece) => {
    const duration = getNumericValue(piece.process_duration);
    const timePenalty = theoreticalCycleTime !== null && theoreticalCycleTime > 0 && duration > theoreticalCycleTime
      ? ((duration - theoreticalCycleTime) / theoreticalCycleTime) * 50
      : 0;

    const alertCount = getPieceAlerts(piece, alertsByPieceId).length;
    const alertPenalty = alertCount * (100 / stepCount);
    const score = clamp(100 - timePenalty - alertPenalty, 0, 100);

    scoreByPieceId.set(String(piece.id), score);
  });

  return scoreByPieceId;
};

const getMasteryIndex = (pieces, scoreByPieceId) => {
  return getAverage(pieces.map((piece) => scoreByPieceId.get(String(piece.id))).filter(Number.isFinite));
};

const getComplianceRate = (pieces, alertsByPieceId, toolStepCount) => {
  if (pieces.length === 0 || toolStepCount <= 0) return null;

  const badToolUsageCount = getAlertsForPieces(pieces, alertsByPieceId).filter((alert) => {
    return alert.type === BAD_TOOL_USAGE_ALERT_TYPE;
  }).length;

  const expectedToolUsages = pieces.length * toolStepCount;

  return clamp(100 * (1 - badToolUsageCount / expectedToolUsages), 0, 100);
};

const getAverageCycleTime = (pieces) => {
  return getAverage(pieces.map((piece) => getNumericValue(piece.process_duration)).filter(Number.isFinite));
};

const getEvolution = (pieces, alertsByPieceId, scoreByPieceId, toolStepCount, rangeKey) => {
  const hourly = rangeKey === 'last_24h';
  const piecesByPeriod = new Map();

  pieces.forEach((piece) => {
    const periodKey = getLocalEvolutionKey(piece.process_end, hourly);

    if (!piecesByPeriod.has(periodKey)) piecesByPeriod.set(periodKey, []);

    piecesByPeriod.get(periodKey).push(piece);
  });

  return [...piecesByPeriod.entries()]
    .sort(([periodA], [periodB]) => periodA.localeCompare(periodB))
    .map(([periodKey, periodPieces]) => {
      return {
        date: periodKey,
        label: formatLocalEvolutionLabel(periodKey, hourly),
        fullLabel: formatLocalEvolutionLabel(periodKey, hourly, true),
        masteryIndex: getMasteryIndex(periodPieces, scoreByPieceId),
        averageCycleTime: getAverageCycleTime(periodPieces),
        complianceRate: getComplianceRate(periodPieces, alertsByPieceId, toolStepCount),
      };
    });
};

const getAlertDistribution = (alerts, pattern) => {
  if (alerts.length === 0) return [];

  const groupedAlerts = new Map();

  alerts.forEach((alert) => {
    const key = alert.type ?? 'unknown';
    groupedAlerts.set(key, (groupedAlerts.get(key) ?? 0) + 1);
  });

  return [...groupedAlerts.entries()]
    .map(([type, count]) => {
      return {
        type,
        name: getVACAeroDashboardAlertLabel(type, pattern),
        count,
        share: count / alerts.length * 100,
      };
    })
    .sort((alertA, alertB) => {
      if (alertB.count !== alertA.count) return alertB.count - alertA.count;

      return alertA.name.localeCompare(alertB.name);
    });
};

const getProcesses = (pieces, alertsByPieceId, pattern) => {
  return sortVACAeroDashboardPieces(pieces).map((piece) => {
    const alerts = sortAlertsAscending(getPieceAlerts(piece, alertsByPieceId)).map((alert) => {
      return {
        ...alert,
        label: getVACAeroDashboardAlertLabel(alert.type, pattern),
      };
    });

    return {
      ...piece,
      alerts,
      status: alerts.length > 0 ? 'Écart' : 'Conforme',
    };
  });
};

export const getVACAeroDashboardData = ({ pieces, alerts, pattern, filters }) => {
  const completedPieces = pieces.filter(isCompletedPiece);
  const filteredPieces = filterVACAeroDashboardPieces(completedPieces, filters);
  const alertsByPieceId = buildAlertsByPieceId(alerts);
  const stepCount = getVACAeroDashboardStepCount(pattern);
  const toolStepCount = getVACAeroDashboardToolStepCount(pattern);
  const theoreticalCycleTime = getVACAeroDashboardTheoreticalCycleTime(pattern);
  const scoreByPieceId = getPieceScoreMap(filteredPieces, alertsByPieceId, stepCount, theoreticalCycleTime);
  const filteredAlerts = getAlertsForPieces(filteredPieces, alertsByPieceId);

  const badToolUsageCount = filteredAlerts.filter((alert) => alert.type === BAD_TOOL_USAGE_ALERT_TYPE).length;

  return {
    stepCount,
    toolStepCount,
    metrics: {
      masteryIndex: getMasteryIndex(filteredPieces, scoreByPieceId),
      complianceRate: getComplianceRate(filteredPieces, alertsByPieceId, toolStepCount),
      alertCount: filteredAlerts.length,
      averageCycleTime: getAverageCycleTime(filteredPieces),
      theoreticalCycleTime,
      badToolUsageCount,
      pieceCount: filteredPieces.length,
    },
    evolution: getEvolution(filteredPieces, alertsByPieceId, scoreByPieceId, toolStepCount, filters.rangeKey),
    alertDistribution: getAlertDistribution(filteredAlerts, pattern),
    processes: getProcesses(filteredPieces, alertsByPieceId, pattern),
  };
};

export const formatVACAeroDashboardDateTime = (value, withSeconds = true) => {
  const date = getDate(value);

  if (Number.isNaN(date.getTime())) return '—';

  const pad = (number) => String(number).padStart(2, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${day}/${month}/${year} ${hours}:${minutes}${withSeconds ? `:${seconds}` : ''}`;
};

export const formatVACAeroDashboardDuration = (seconds) => {
  if (!Number.isFinite(seconds)) return '—';

  return formatSecondsToHMS(seconds, 'long', true).trim();
};

export const formatVACAeroDashboardPercentage = (value) => {
  if (!Number.isFinite(value)) return '—';

  return `${value.toFixed(1).replace('.', ',')} %`;
};

export const formatVACAeroDashboardIndex = (value) => {
  if (!Number.isFinite(value)) return '—';

  return `${Math.round(value)} / 100`;
};